<script setup>
import CreateWebsiteVariantAction from '@frontJs/components/actions/projectManagement/customers/CreateWebsiteVariantAction.vue';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, all } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    all: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'tenant.name',
            header: () => 'Client',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'id',
            header: () => 'ID',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'shortName',
            header: () => 'Nom court',
            cell: (props) =>
                props.row.original.shortName
                    ? h(TextCell, {
                          text: props.row.original.shortName,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseUrl',
            header: () => 'URL de base',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.baseUrl,
                    href: props.row.original.baseUrl,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'isDefault',
            header: () => 'Par défaut',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isDefault,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'targets',
            header: () => 'Cibles',
            enableSorting: false,
            cell: (props) => {
                const targets = props.row.original.targets;

                return h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                DisplayContentAction,
                                { title: 'Cibles' },
                                {
                                    trigger: () =>
                                        h(
                                            'span',
                                            {
                                                class: 'cursor-pointer text-blue-500',
                                            },
                                            `${targets.length} cible(s)`,
                                        ),
                                    complexContent: () =>
                                        h(
                                            'ul',
                                            { class: 'mt-2 list-disc pl-8' },
                                            targets.map((target, targetKey) =>
                                                h(
                                                    'li',
                                                    { key: targetKey },
                                                    `${target.country}${target.language ? ` - ${target.language}` : ''}`,
                                                ),
                                            ),
                                        ),
                                },
                            ),
                    },
                );
            },
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.customer.websiteVariant',
                                    {
                                        tenant: tenant
                                            ? tenant.id
                                            : props.row.original.tenant.id,
                                        websiteVariant: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.customer.postWebsiteVariants', {
        tenant: tenant ? tenant.id : null,
        all,
    }),
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        'tenant.name': !tenant,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                v-if="tenant"
                size="small"
                :left-icon="PlusIcon"
                :href="
                    route('front.projectManagement.customer.websiteVariant', {
                        tenant: tenant.id,
                    })
                "
            >
                Ajouter
            </ActionButton>

            <CreateWebsiteVariantAction
                v-else
                :tenants="globalData.tenants ?? []"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="PlusIcon"
                        type="primary"
                    >
                        Ajouter
                    </ActionButton>
                </template>
            </CreateWebsiteVariantAction>
        </template>
    </DataTableLayout>
</template>
